import React from 'react';
import { ZfAccordionItem } from '@zafire/react';
import i18n from '../../../i18n';
import { ServiceAgreementV2 } from '../eBankingTempTypes';
import { getProductDisplayName } from '../mappers/serviceAgreementToTreeMapper';
import { DetailsBlock } from './blocks/DetailsBlock';
import { SafeDepositBoxConfigurationBlock } from './blocks/SafeDepositBoxConfigurationBlock';
import { ServiceRoleUsersBlock } from './blocks/ServiceRoleUsersBlock';
import { RevisionHistoryBlock } from './blocks/RevisionHistoryBlock';
import { ContactNotesBlock } from './blocks/ContactNotesBlock';
import {
  isInvestmentProductServiceDefinitionId,
  isActiveReferralServiceDefinitionId,
  isSafeClearingServiceDefinitionId,
  isSafeDepositBoxServiceDefinitionId,
  isWealthPlanningServiceDefinitionId,
  showsServiceRoleUsersTable,
} from '../privateBankingServiceDefinitionIds';
import { ProductDetailsBlock } from './blocks/ProductDetailsBlock';
import { ServiceModel } from 'pco-product-service-internal-consumer';

export type BlockConfig = {
  type: string;
  title: string;
  render: (product: ServiceAgreementV2) => React.ReactElement;
};

const details: BlockConfig = {
  type: 'details',
  title: 'Details',
  render: (product) => <DetailsBlock product={product} />,
};

const serviceRoleUsers = (productName: string | undefined): BlockConfig => ({
  type: 'service-role-users',
  title: `${productName || '-'} users`,
  render: (product) => (
    <ServiceRoleUsersBlock serviceRoles={product.serviceRoles} serviceDefinitionId={product.serviceDefinitionId} />
  ),
});

const safeDepositBox: BlockConfig = {
  type: 'safe-deposit-box',
  title: i18n.t('pb.productdetails.safedepositbox.title'),
  render: (product) => (
    <div data-testid="safe-deposit-box-details-content">
      <SafeDepositBoxConfigurationBlock product={product} />
      <ServiceRoleUsersBlock serviceRoles={product.serviceRoles} serviceDefinitionId={product.serviceDefinitionId} />
    </div>
  ),
};

const contactNotes: BlockConfig = {
  type: 'contact-notes',
  title: i18n.t('pb.contactnotes.block.title', 'Contact note'),
  render: (product) => <ContactNotesBlock serviceAgreementId={product.serviceAgreementId} />,
};

const revisionHistory: BlockConfig = {
  type: 'revision-history',
  title: 'Product revision history',
  render: (product) => <RevisionHistoryBlock serviceAgreementId={product.serviceAgreementId} />,
};

const activeProduct: BlockConfig = {
  type: 'active-product-detail',
  title: 'Details former product',
  render: (product) => <ProductDetailsBlock product={product} serviceModel={product.serviceModel as ServiceModel} />,
};

const draftProduct: BlockConfig = {
  type: 'draft-product-detail',
  title: 'Details',
  render: (product) => (
    <ProductDetailsBlock product={product} serviceModel={product.draftServiceModel as ServiceModel} />
  ),
};

const defaultBlocks: BlockConfig[] = [details, contactNotes, revisionHistory];

export const getProductDetailsBlocks = (product: ServiceAgreementV2): BlockConfig[] => {
  if (
    isSafeClearingServiceDefinitionId(product.serviceDefinitionId) ||
    isWealthPlanningServiceDefinitionId(product.serviceDefinitionId) ||
    isActiveReferralServiceDefinitionId(product.serviceDefinitionId)
  ) {
    return [details, revisionHistory];
  }

  if (isSafeDepositBoxServiceDefinitionId(product.serviceDefinitionId)) {
    return [details, safeDepositBox, contactNotes, revisionHistory];
  }

  if (showsServiceRoleUsersTable(product.serviceDefinitionId)) {
    return [details, serviceRoleUsers(getProductDisplayName(product)), contactNotes, revisionHistory];
  }

  if (isInvestmentProductServiceDefinitionId(product.serviceDefinitionId) && product.draftServiceModel) {
    return [draftProduct, activeProduct, contactNotes, revisionHistory];
  }

  return defaultBlocks;
};

export const BlockRenderer: React.FC<{ config: BlockConfig; product: ServiceAgreementV2 }> = ({ config, product }) => (
  <ZfAccordionItem state="active" titleText={config.title}>
    {config.render(product)}
  </ZfAccordionItem>
);
